import * as Icons from "lucide-react";
import { CTA, GlassCard, PageHeader, SectionHeading, Shell } from "../components/site/Primitives";

const PRINCIPLES = [
  ["Operationally useful", "AI only matters when it changes what happens on a Tuesday afternoon. We build for daily operations, not demos."],
  ["Honest about scope", "If an integration isn't connected, the platform says so. We never present configuration as capability."],
  ["One system, not ten tools", "Shared data and one customer record beat a stack of disconnected apps."],
  ["Owned by the client", "Provider accounts, data and workflows stay with the business we build for."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        testId="about-title"
        eyebrow="About"
        title="Elvoria AI helps businesses replace repetitive manual processes with intelligent systems."
        sub="We build AI-powered business operating systems: the receptionist, the CRM, the calendar, the follow-up and the reporting, working as one."
      />

      <section className="py-20">
        <Shell>
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <SectionHeading
                testId="about-mission-heading"
                eyebrow="Mission"
                title="Make advanced AI operationally useful for everyday businesses."
                sub="Most businesses don't need a research lab. They need calls answered, leads followed up, appointments booked and a reliable record of what happened."
              />
              <p className="mt-8 text-sm leading-relaxed text-elv-dim">
                Elvoria AI exists because the gap between what AI can do and what businesses actually run on is
                still wide. Closing it is not a model problem — it is an operations problem: scoping the agent,
                connecting the calendar, writing back to the CRM, and making sure a human takes over at the right
                moment.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-elv-dim">
                We are a new agency and we say so plainly. There are no invented client logos, testimonials or
                performance statistics anywhere on this site. What we show instead is the working platform.
              </p>
            </div>
            <GlassCard testId="about-facts" hover={false} className="p-6 sm:p-8">
              <p className="text-[11px] uppercase tracking-[0.24em] text-elv-green">What we do</p>
              <ul className="mt-6 space-y-4">
                {[
                  "Automation audits and workflow design",
                  "AI receptionist and voice agent configuration",
                  "CRM, pipeline and customer record setup",
                  "Appointment and follow-up automation",
                  "Unified communication inbox",
                  "Analytics and AI reporting",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-elv-dim">
                    <Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
                    {i}
                  </li>
                ))}
              </ul>
              <div className="mt-8 border-t border-elv-line pt-6">
                <CTA to="/contact" variant="ghost" testId="about-contact-cta">
                  Talk to us
                </CTA>
              </div>
            </GlassCard>
          </div>
        </Shell>
      </section>

      <section className="border-y border-elv-line bg-[#070A0F] py-20">
        <Shell>
          <SectionHeading testId="about-principles-heading" eyebrow="Principles" title="How we work." />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map(([t, d], i) => (
              <GlassCard key={t} testId={`about-principle-${i}`} className="p-6">
                <span className="font-mono text-[11px] text-elv-green">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-4 text-sm font-semibold text-elv-text">{t}</p>
                <p className="mt-2.5 text-xs leading-relaxed text-elv-dim">{d}</p>
              </GlassCard>
            ))}
          </div>
        </Shell>
      </section>
    </div>
  );
}
